import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import EmailEditor from '../EmailEditor';
import { formatMarketingSenderPostalAddress } from '../settings/unsubscribe/templates';
import { getMarketingSendersRawList } from '../utils/marketingSenderOptions';
import {
    createMarketingEmailTemplate,
    updateMarketingEmailTemplate,
} from 'services/MailingService';
import { listSenders } from 'services/SenderService';
import { getLiteral } from 'utils/getLiteral';
import { errorToast, successToast } from 'utils/toast';

import { MARKETING_TEMPLATES_PATHS } from './marketingTemplatesPaths';
import { useMarketingTemplatesRefresh } from './MarketingTemplatesRefreshContext';

import './TemplateVisualEditor.scss';

const normalizeTopolConfig = (strJson) => {
    if (strJson == null || strJson === '') {
        return '';
    }
    if (typeof strJson === 'string') {
        return strJson;
    }
    try {
        return JSON.stringify(strJson);
    } catch {
        return '';
    }
};

const TemplateVisualEditor = memo(
    ({ isOpen = false, strJson, initialTitle = '', templateId, onExit }) => {
        const navigate = useNavigate();
        const { requestMarketingTemplatesRefresh } = useMarketingTemplatesRefresh();

        const isEditMode =
            templateId !== null && templateId !== undefined && String(templateId).length > 0;

        const initialTitleNormalized = useMemo(() => {
            return typeof initialTitle === 'string' ? initialTitle.trim() : '';
        }, [initialTitle]);

        const [currentTitle, setCurrentTitle] = useState(initialTitleNormalized);

        useEffect(() => {
            if (isOpen) {
                setCurrentTitle(initialTitleNormalized);
            }
        }, [isOpen, initialTitleNormalized]);

        const emailConfig = useMemo(() => normalizeTopolConfig(strJson), [strJson]);

        const [footerPreview, setFooterPreview] = useState({
            senderName: '',
            senderAddress: '',
            footerHere: getLiteral('label_marketing_campaigns_unsubscribe_here'),
            linkHref: '#',
        });

        useEffect(() => {
            if (!isOpen) {
                return undefined;
            }

            let cancelled = false;

            listSenders()
                .then((data) => {
                    if (cancelled) {
                        return;
                    }
                    const raw = getMarketingSendersRawList(data);
                    const defaultSender = raw.find((sender) => sender.isDefault) ?? raw[0];
                    setFooterPreview({
                        senderName: defaultSender?.name ? String(defaultSender.name).trim() : '',
                        senderAddress: formatMarketingSenderPostalAddress(defaultSender),
                        footerHere: getLiteral('label_marketing_campaigns_unsubscribe_here'),
                        linkHref: '#',
                    });
                })
                .catch(() => {
                    if (!cancelled) {
                        setFooterPreview({
                            senderName: '',
                            senderAddress: '',
                            footerHere: getLiteral('label_marketing_campaigns_unsubscribe_here'),
                            linkHref: '#',
                        });
                    }
                });

            return () => {
                cancelled = true;
            };
        }, [isOpen]);

        const handleTitleChange = useCallback((newTitle) => {
            setCurrentTitle(newTitle);
        }, []);

        const handleExit = useCallback(() => {
            onExit?.();
        }, [onExit]);

        const handleSave = useCallback(
            ({ html, topolJson }) => {
                const name =
                    typeof currentTitle === 'string' && currentTitle.trim()
                        ? currentTitle.trim()
                        : getLiteral('label_email_template');

                const payload = {
                    name,
                    html,
                    ...(topolJson ? { topolConfig: topolJson } : {}),
                };

                const savePromise = isEditMode
                    ? updateMarketingEmailTemplate(templateId, payload)
                    : createMarketingEmailTemplate(payload);

                savePromise
                    .then(() => {
                        successToast({
                            title: getLiteral(
                                isEditMode
                                    ? 'title_success_template_updated'
                                    : 'title_success_template_created',
                            ),
                            text: getLiteral(
                                isEditMode
                                    ? 'label_success_template_updated'
                                    : 'label_success_template_created',
                            ),
                        });
                        if (isEditMode) {
                            requestMarketingTemplatesRefresh();
                        }
                        navigate(MARKETING_TEMPLATES_PATHS.BASE_LIST);
                        onExit?.();
                    })
                    .catch(() => {
                        errorToast({
                            title: getLiteral(
                                isEditMode
                                    ? 'title_error_template_updated'
                                    : 'title_error_template_created',
                            ),
                            text: getLiteral(
                                isEditMode
                                    ? 'label_error_template_updated'
                                    : 'label_error_template_created',
                            ),
                        });
                    });
            },
            [
                navigate,
                currentTitle,
                onExit,
                isEditMode,
                requestMarketingTemplatesRefresh,
                templateId,
            ],
        );

        if (!isOpen) {
            return null;
        }

        return (
            <div className="marketing-templates-visual-editor">
                <EmailEditor
                    isOpen={isOpen}
                    emailConfig={emailConfig}
                    title={currentTitle}
                    editableTitle={true}
                    footerPreview={footerPreview}
                    onTitleChange={handleTitleChange}
                    onSave={handleSave}
                    onExit={handleExit}
                />
            </div>
        );
    },
);

TemplateVisualEditor.displayName = 'TemplateVisualEditor';

TemplateVisualEditor.propTypes = {
    isOpen: PropTypes.bool,
    strJson: PropTypes.string,
    initialTitle: PropTypes.string,
    templateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onExit: PropTypes.func,
};

export default TemplateVisualEditor;
